import {
  DATE_FORMATTER_DAYS,
  DATE_FORMATTER_HOURS_MINUTES_ONLY,
  DATE_FORMATTER_HOURS_ONLY,
  DATE_FORMATTER_MINUTES,
  DATE_FORMATTER_MONTH_DAY_ONLY,
} from "utils/constants";
import { Instant, IsoFields, ZonedDateTime, ZoneId } from "@js-joda/core";
import { toZonedDateTime } from "utils/time-utils";
import { AggregationPeriod, DataDisplayType } from "./ValueChangeIndicatorTypes";

export const getMainClassName = (dataDisplayType: DataDisplayType, reportMode?: boolean, displayInline?: boolean) => {
  const currentClassName = `last-value-indicator${reportMode ? "-report-mode" : ""}`;

  if (dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART) {
    return `${currentClassName}-horizontal`;
  }
  if (displayInline) {
    return `${currentClassName}-inline`;
  }
  return currentClassName;
};

export const getTitleClassName = (dataDisplayType: DataDisplayType, displayInline?: boolean): string => {
  const baseTitle = dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ? "title-horizontal" : "title";

  const fontWeight = displayInline || dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ? "font-bold" : "font-medium";

  return `${baseTitle} ${fontWeight}`;
};

export const getDiffClassName = (
  isNeutral: boolean,
  dataDisplayType: DataDisplayType,
  useDiffIndicatorColors?: boolean
): string => {
  const baseDiff = dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ? "last-value-diff-horizontal" : "last-value-diff";

  const classNames = [
    baseDiff,
    "flex flex-row align-items-center justify-content-end",
    useDiffIndicatorColors && !isNeutral && "indicator-colors",
    isNeutral && "neutral",
  ]
    .filter(Boolean)
    .join(" ");

  return classNames;
};

export const getUpToPeriodLabel = (upToUnit: AggregationPeriod, referenceTime: Instant | undefined, zoneId: ZoneId) => {
  const currentAlignUnit = (upToUnit ?? AggregationPeriod.MONTHS).toString().toUpperCase();
  const now = toZonedDateTime(referenceTime || Instant.now(), zoneId);

  switch (currentAlignUnit) {
    case AggregationPeriod.MINUTES:
      return now.format(DATE_FORMATTER_HOURS_MINUTES_ONLY);
    case AggregationPeriod.HOURS:
      return now.format(DATE_FORMATTER_HOURS_ONLY);
    case AggregationPeriod.DAYS:
      return now.format(DATE_FORMATTER_DAYS);
    case AggregationPeriod.WEEKS: {
      const week = now.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
      return `W${week}`;
    }
    case AggregationPeriod.MONTHS: {
      const lastDayOfMonth = now.withDayOfMonth(1).minusDays(1);
      return lastDayOfMonth.format(DATE_FORMATTER_MONTH_DAY_ONLY);
    }
    case AggregationPeriod.YEARS:
      return String(now.year());
    default:
      return now.format(DATE_FORMATTER_MINUTES);
  }
};

export const getMonthYearComparison = (
  dataset: { key: string; value: number }[],
  isLastFinishedMonth: boolean,
  zone: ZoneId
) => {
  const now = ZonedDateTime.now(zone);

  const firstDayOfCurrentMonth = now.withDayOfMonth(1).withHour(0).withMinute(0).withSecond(0).withNano(0);

  let lastFinished: ZonedDateTime;

  if (isLastFinishedMonth) {
    lastFinished = firstDayOfCurrentMonth.minusNanos(1);
  } else {
    lastFinished = firstDayOfCurrentMonth;
  }

  const targetYear = lastFinished.year();
  const targetMonth = lastFinished.monthValue();

  const findValue = (y: number, m: number) => {
    return (
      dataset.find(d => {
        const dt = ZonedDateTime.parse(d.key).withZoneSameInstant(zone);
        return dt.year() === y && dt.monthValue() === m;
      })?.value ?? null
    );
  };

  const curr = findValue(targetYear, targetMonth);
  const prev = findValue(targetYear - 1, targetMonth);
  const currValueKey = `${targetYear}-${targetMonth}`;
  const prevValueKey = `${targetYear - 1}-${targetMonth}`;

  return { curr, prev, currValueKey, prevValueKey };
};
